import { featureCollection } from "@turf/helpers";
import squareGrid from "@turf/square-grid";
import bbox from "@turf/bbox";
import intersect from "@turf/intersect";

/**
 * 
 * 
 * 
 * Mengecek apakah dua polygon bersinggungan, sekecil apapun irisannya,
 * termasuk di area edge/tepi polygon.
 *
 * @param {import("geojson").Feature<import("geojson").Polygon>} a
 * @param {import("geojson").Feature<import("geojson").Polygon>} b
 * @returns {boolean}
 */
export const polygonsCrossing = (a, b) => {
  try {
    return intersect(featureCollection([a, b])) !== null;
  } catch {
    return false;
  }
}

/**
 * Mengkonversi array polygon yang tumpang tindih menjadi
 * deduplicated square grid sebagai array GeoJSON Feature<Polygon>.
 */
export function polygonsToDeduplicatedGrid(
  polygons,
  cellSide,
  units = "kilometers"
) {
  if (polygons.length === 0) return [];

  // turf.squareGrid tidak support "meters", konversi ke kilometers
  let normalizedCellSide = cellSide;
  let normalizedUnits = units;
  if (units === "meters") {
    normalizedCellSide = cellSide / 1000;
    normalizedUnits = "kilometers";
  }

  // 1. Hitung bbox gabungan semua polygon + padding 1 cell agar tepi ikut ter-generate
  const padding = normalizedCellSide * 0.01; // ~1% cell size dalam derajat
  const combinedBBox = polygons.reduce(
    (acc, polygon) => {
      const [minX, minY, maxX, maxY] = bbox(polygon);
      return [
        Math.min(acc[0], minX),
        Math.min(acc[1], minY),
        Math.max(acc[2], maxX),
        Math.max(acc[3], maxY),
      ];
    },
    [Infinity, Infinity, -Infinity, -Infinity]
  );

  const paddedBBox = [
    combinedBBox[0] - padding,
    combinedBBox[1] - padding,
    combinedBBox[2] + padding,
    combinedBBox[3] + padding,
  ];

  // 2. Buat square grid yang menutupi seluruh bbox
  const grid = squareGrid(paddedBBox, normalizedCellSide, {
    units: normalizedUnits,
  });

  // 3. Filter: hanya ambil cell yang bersinggungan dengan minimal 1 polygon
  const seen = new Map();

  for (const cell of grid.features) {
    const [minX, minY] = bbox(cell);
    const key = `${minX.toFixed(8)},${minY.toFixed(8)}`;

    if (seen.has(key)) continue;

    const intersectsAny = polygons.some((polygon) =>
      polygonsCrossing(cell, polygon)
    );

    if (intersectsAny) {
      seen.set(key, cell);
    }
  }

  const output = Array.from(seen.values());
  return output
}

export const is_grid_already_covered = (grid_feature, selected_features, fetched_uuids) => {
  const intersecting_polygons = selected_features.filter(polygon =>
    polygonsCrossing(grid_feature, polygon)
  );

  // Kalau semua polygon yang di-intersect grid ini udah di-fetch, skip
  return intersecting_polygons.every(polygon =>
    fetched_uuids.has(polygon.properties.uuid)
  );
};


export const show_to_map = (map_object, features, id) => {
  const source_id = id || "geojson_sementara"

  const geojson = {
    type: 'FeatureCollection',
    features: features
  }

  if (map_object.getSource(source_id)) {
    map_object.getSource(source_id).setData(geojson);
  } else {
    map_object.addSource(source_id, {
      type: "geojson",
      data: geojson
    });
  }

  // paint default for point
  let paint_obj = {
    type: "circle",
    paint: {
      "circle-color": "#ffffff",
      "circle-radius": 7,
      "circle-stroke-width": 1,
      "circle-stroke-color": "#000000",
    }
  }
  if (map_object.getLayer(source_id)) map_object.removeLayer(source_id);
  if (features?.[0]?.geometry?.type === "Polygon") {
    paint_obj = {
      type: "fill",
      paint: {
        "fill-color": "#00ff00",
        "fill-opacity": 0.2,
        "fill-outline-color": "#019371",
      },
    }
  }
  map_object.addLayer({
    id: source_id,
    source: source_id,
    layout: { visibility: "visible" },
    ...paint_obj
  });


  if (map_object.getLayer(`${source_id}_line`)) map_object.removeLayer(`${source_id}_line`)
  if (features?.[0]?.geometry?.type !== "Point") {
    map_object.addLayer({
      id: `${source_id}_line`,
      source: source_id,
      type: "line",
      paint: {
        "line-color": "#800020",
        "line-width": 2,
        "line-opacity": 1,
      },
      layout: {
        visibility: "visible",
      },
    });
  }
}

export const remove_from_map = (map_object, id) => {
  const source_id = id || "geojson_sementara"

  if (map_object.getLayer(`${source_id}_line`)) map_object.removeLayer(`${source_id}_line`)
  if (map_object.getLayer(source_id)) map_object.removeLayer(source_id)
  if (map_object.getSource(source_id)) map_object.removeSource(source_id);
}